(function () {
  'use strict';
  // Detailed logging during dev — search browser console for "[gas-card]" to follow execution.
  var LOG = function () {
    try { console.log.apply(console, ['[gas-card]'].concat([].slice.call(arguments))); } catch (e) {}
  };
  LOG('script loaded');

  if (window.__GasCardRewardInitialized) { LOG('already initialized, bailing'); return; }
  window.__GasCardRewardInitialized = true;

  // Backend lives in dyc-analytics Vercel project — co-located with the admin
  // dashboard so the team can see issued rewards alongside other analytics.
  var CATALOG_ENDPOINT = 'https://dyc-analytics.vercel.app/api/gas-card/catalog';
  var STATE = { LOCKED: 'LOCKED', UNLOCKED: 'UNLOCKED', PICKED: 'PICKED' };

  function $(root, sel) { return root.querySelector(sel); }
  function fmtMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function loadJSON(url) {
    return fetch(url, { credentials: 'omit' }).then(function (r) {
      if (!r.ok) throw new Error(url + ' ' + r.status);
      return r.json();
    });
  }

  function postCartAttribute(name, value) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: (function () { var o = {}; o[name] = value; return o; })() })
    }).then(function (r) { return r.json(); });
  }

  function GasCardReward(root) {
    LOG('constructor: root found', root);
    this.root = root;
    var configEl = root.querySelector('[data-gas-config]');
    var configText = configEl ? configEl.textContent : root.getAttribute('data-config');
    LOG('constructor: config text length', (configText || '').length, 'has script tag:', !!configEl);
    try {
      this.config = JSON.parse(configText);
    } catch (e) {
      console.error('[gas-card] bad config JSON', e, configText);
      return;
    }
    LOG('constructor: config parsed', this.config);
    this.thresholdCents = this.config.thresholdCents;
    this.state = STATE.LOCKED;
    this.catalog = null;
    this.picker = $(root, '[data-gas-picker]');
    this.titleEl = $(root, '[data-gas-bar-title]');
    this.subtextEl = $(root, '[data-gas-bar-subtext]');
    this.pctEl = $(root, '[data-gas-bar-pct]');
    this.fillEl = $(root, '[data-gas-bar-fill]');
    this.pickerListEl = $(root, '[data-gas-picker-list]');
    this.clearEl = $(root, '[data-gas-clear]');

    if (this.config.colors) {
      this.root.style.setProperty('--gas-unlocked-accent', this.config.colors.unlockedAccent);
      this.root.style.setProperty('--gas-card-accent', this.config.colors.cardAccent);
    }

    var self = this;
    this.fetchCatalog().then(function () { self.renderPicker(); });
    this.refreshFromCart();
  }

  GasCardReward.prototype.fetchCatalog = function () {
    var self = this;
    var cached = sessionStorage.getItem('gas_card_catalog_v2');
    if (cached) {
      try { self.catalog = JSON.parse(cached).brands; return Promise.resolve(); } catch (e) {}
    }
    return loadJSON(CATALOG_ENDPOINT).then(function (data) {
      self.catalog = data.brands;
      sessionStorage.setItem('gas_card_catalog_v2', JSON.stringify(data));
    }).catch(function (e) { console.warn('[gas-card] catalog fetch failed', e); });
  };

  // Text initial is the FALLBACK rendered in the colored chip; the brand logo
  // <img> is layered on top via CSS. If the image fails to load (Brandfetch
  // down, CSP block, network), the chip + initial show through unchanged.
  var BRAND_INITIAL = { pilot: 'PFJ', chevron: 'C+T', shell: 'S', visa: 'VISA' };

  GasCardReward.prototype.renderPicker = function () {
    if (!this.catalog) return;
    var enabled = this.config.enabledBrands;
    var html = '';
    var self = this;
    enabled.forEach(function (key) {
      var b = self.catalog[key];
      if (!b) return;
      var initial = BRAND_INITIAL[key] || (b.name || '?').slice(0, 1);
      // Prefer locally-hosted PNGs (Liquid-rendered Shopify CDN URLs via config.logoUrls)
      // over the catalog's logo_url. Local assets are hand-picked clean PNGs without
      // the avatar-circle treatment Brandfetch bakes into its default endpoint.
      var configLogo = self.config.logoUrls && self.config.logoUrls[key];
      var logoSrc = configLogo || b.logo_url;
      var logoImg = logoSrc
        ? '<img class="gas-brand__chip-img" src="' + logoSrc + '" alt="" onerror="this.remove()">'
        : '';
      html += [
        '<li>',
        '  <button type="button" class="gas-brand" aria-pressed="false" data-brand="' + key + '">',
        '    <span class="gas-brand__chip gas-brand__chip--' + key + '">' + initial + logoImg + '</span>',
        '    <div class="gas-brand__body">',
        '      <span class="gas-brand__name">' + b.name + '</span>',
        (self.config.showBrandDescriptions ? '      <span class="gas-brand__desc">' + b.desc + '</span>' : ''),
        '    </div>',
        '    <span class="gas-brand__check" aria-hidden="true">✓</span>',
        '  </button>',
        '</li>'
      ].join('\n');
    });
    this.pickerListEl.innerHTML = html;
    var self2 = this;
    this.pickerListEl.querySelectorAll('.gas-brand').forEach(function (btn) {
      btn.addEventListener('click', function () { self2.pickBrand(btn.getAttribute('data-brand')); });
    });
    // Re-apply the current pick: refreshFromCart may have run markBrandSelected
    // BEFORE renderPicker (catalog fetch is async; cart eval is sync) — in which
    // case the buttons it tried to mark didn't exist yet. Without this, after a
    // cart re-render the buttons all end up aria-pressed="false", breaking
    // the CSS :has() rule that collapses non-selected chips.
    if (this.state === STATE.PICKED) {
      var pickedBrand = this.root.getAttribute('data-picked-brand');
      if (pickedBrand) this.markBrandSelected(pickedBrand);
    }
  };

  GasCardReward.prototype.refreshFromCart = function () {
    // Liquid renders the current subtotal + picked brand into root data-* attrs
    // on every cart-drawer section re-render, so the DOM is the source of truth.
    // Fall back to /cart.js only if those attrs are missing (defensive).
    var subtotalAttr = this.root.getAttribute('data-subtotal-cents');
    var brandAttr    = this.root.getAttribute('data-picked-brand') || '';
    if (subtotalAttr !== null && subtotalAttr !== '') {
      var subtotal = parseInt(subtotalAttr, 10);
      if (!isNaN(subtotal)) {
        LOG('refreshFromCart: from DOM', { subtotal: subtotal, brand: brandAttr });
        this.evaluate(subtotal, brandAttr ? { gas_card_brand: brandAttr } : {});
        return Promise.resolve();
      }
    }
    var self = this;
    LOG('refreshFromCart: data-subtotal-cents missing/invalid, fetching /cart.js');
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        LOG('refreshFromCart: cart', { subtotal: cart.items_subtotal_price, attributes: cart.attributes, item_count: cart.item_count });
        self.evaluate(cart.items_subtotal_price, cart.attributes || {});
      })
      .catch(function (e) { console.warn('[gas-card] cart fetch failed', e); });
  };

  GasCardReward.prototype.evaluate = function (subtotalCents, attributes) {
    var pct = Math.min(100, Math.round((subtotalCents / this.thresholdCents) * 100));
    LOG('evaluate: subtotal=' + subtotalCents + ' threshold=' + this.thresholdCents + ' pct=' + pct + ' fillEl?=' + !!this.fillEl + ' pctEl?=' + !!this.pctEl);
    if (this.pctEl) this.pctEl.textContent = pct + '%';
    if (this.fillEl) this.fillEl.style.width = pct + '%';

    var brand = attributes && attributes.gas_card_brand;
    var meetsThreshold = subtotalCents >= this.thresholdCents;

    if (!meetsThreshold) return this.transitionTo(STATE.LOCKED, subtotalCents, null);
    if (brand) return this.transitionTo(STATE.PICKED, subtotalCents, brand);
    return this.transitionTo(STATE.UNLOCKED, subtotalCents, null);
  };

  GasCardReward.prototype.transitionTo = function (newState, subtotalCents, brand) {
    this.state = newState;
    this.root.setAttribute('data-state', newState);
    // Mirror the active pick into a DOM attribute so pickBrand() can detect a
    // re-tap of the already-selected chip without consulting JS state alone
    // (state survives across cart re-renders since Liquid puts data-picked-brand
    // back into the new root).
    this.root.setAttribute('data-picked-brand', brand || '');
    var copy = this.config.copy;

    // Update the picker title to communicate "tap to change" on PICKED state.
    // This is visible on all devices (no hover dependency), giving mobile users
    // an obvious affordance to switch their selection. UNLOCKED state restores
    // the original "Choose your gas card" prompt.
    var pickerTitleEl = this.root.querySelector('.gas-picker__title');
    if (pickerTitleEl) {
      pickerTitleEl.textContent = newState === STATE.PICKED
        ? 'Tap to change your selection'
        : 'Choose your gas card';
    }

    if (newState === STATE.LOCKED) {
      var remaining = Math.max(0, this.thresholdCents - subtotalCents);
      this.titleEl.textContent = '⛽ ' + copy.lockedHeadline;
      this.subtextEl.textContent = (copy.lockedSubtext || '').replace('[amount]', fmtMoney(remaining));
      this.picker.setAttribute('data-open', 'false');
      if (this.clearEl) this.clearEl.setAttribute('hidden', '');
    } else if (newState === STATE.UNLOCKED) {
      this.titleEl.textContent = copy.unlockedHeadline;
      this.subtextEl.textContent = copy.unlockedSubtext;
      this.picker.setAttribute('data-open', this.config.pickerDefaultExpanded ? 'true' : 'false');
      if (this.clearEl) this.clearEl.setAttribute('hidden', '');
      this.markBrandSelected(null);
    } else if (newState === STATE.PICKED) {
      var brandLabel = (this.catalog && this.catalog[brand] && this.catalog[brand].name) || brand;
      this.titleEl.textContent = (copy.pickedHeadline || '').replace('[brand]', brandLabel);
      this.subtextEl.textContent = copy.pickedSubtext;
      this.picker.setAttribute('data-open', 'true');
      if (this.clearEl) this.clearEl.removeAttribute('hidden');
      this.markBrandSelected(brand);
    }
    document.dispatchEvent(new CustomEvent('dyc:gas-card-state', { detail: { state: newState, brand: brand } }));
  };

  GasCardReward.prototype.markBrandSelected = function (brand) {
    // Both jobs in one pass:
    //   1. Set aria-pressed for a11y + visual selected-state border
    //   2. Toggle the gas-li--hidden class on each <li> so the CSS animation
    //      collapses non-selected rows when we're in PICKED state.
    var btns = this.pickerListEl.querySelectorAll('.gas-brand');
    var inPicked = this.state === STATE.PICKED;
    btns.forEach(function (btn) {
      var isPicked = btn.getAttribute('data-brand') === brand;
      btn.setAttribute('aria-pressed', isPicked ? 'true' : 'false');
      var li = btn.closest('li');
      if (!li) return;
      if (inPicked && !isPicked) {
        li.classList.add('gas-li--hidden');
      } else {
        li.classList.remove('gas-li--hidden');
      }
    });
  };

  GasCardReward.prototype.pickBrand = function (brand) {
    var self = this;
    // Toggle: tapping the already-selected chip clears the pick, re-opening
    // the picker (the other two chips animate back in via CSS).
    if (this.state === STATE.PICKED && this.root.getAttribute('data-picked-brand') === brand) {
      this.clearPick();
      return;
    }
    var prev = this.state;
    this.transitionTo(STATE.PICKED, this.thresholdCents, brand);
    postCartAttribute('gas_card_brand', brand).catch(function () {
      self.transitionTo(prev, self.thresholdCents, null);
      console.warn('[gas-card] saving pick failed');
    });
  };

  GasCardReward.prototype.clearPick = function () {
    var self = this;
    var subtotalNow = self.thresholdCents;
    self.transitionTo(STATE.UNLOCKED, subtotalNow, null);
    postCartAttribute('gas_card_brand', '').catch(function () {
      console.warn('[gas-card] clearing pick failed');
    });
  };

  window.GasCardReward = { instance: null };

  function handleCartUpdate(detail) {
    // Re-bind first: cart-drawer.js may have just replaced the cart drawer DOM
    // via Section Rendering API, leaving the previous instance pointing at
    // detached elements. init() is idempotent and will rebind if root changed.
    init();
    if (!window.GasCardReward.instance) return;
    var cart = (detail && detail.cartData) || detail || {};
    if (typeof cart.items_subtotal_price === 'number') {
      window.GasCardReward.instance.evaluate(cart.items_subtotal_price, cart.attributes || {});
    } else {
      window.GasCardReward.instance.refreshFromCart();
    }
  }

  // Subscribe to the theme's existing pub/sub bus (used by cart-drawer.js) so we
  // react to every cart mutation without modifying cart-drawer.js itself.
  function subscribeToCartUpdates() {
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate || 'cart-update', handleCartUpdate);
    }
  }

  // Also listen to a generic dyc-namespaced event for any non-theme code that wants to nudge us
  document.addEventListener('dyc:cart-updated', function (e) { handleCartUpdate(e.detail); });

  // Shopify customizer events — fire when a section is re-rendered in the editor.
  // Without these listeners the bar stays stuck at its initial render whenever the
  // user changes a customizer setting (which re-injects fresh DOM).
  document.addEventListener('shopify:section:load',    function () { init(); if (window.GasCardReward.instance) window.GasCardReward.instance.refreshFromCart(); });
  document.addEventListener('shopify:section:select',  function () { init(); if (window.GasCardReward.instance) window.GasCardReward.instance.refreshFromCart(); });
  document.addEventListener('shopify:section:reorder', function () { init(); if (window.GasCardReward.instance) window.GasCardReward.instance.refreshFromCart(); });

  function init() {
    var root = document.querySelector('[data-gas-card-root]');
    if (!root) { LOG('init: [data-gas-card-root] not found yet'); return false; }
    if (window.GasCardReward.instance && window.GasCardReward.instance.root === root) {
      LOG('init: already wired to this root');
      return true;
    }
    LOG('init: wiring new instance');
    window.GasCardReward.instance = new GasCardReward(root);
    return true;
  }

  function bootstrap() {
    LOG('bootstrap: readyState=' + document.readyState);
    if (init()) {
      subscribeToCartUpdates();
      return;
    }
    // Cart drawer might be re-rendered after page load (e.g., by cart-drawer.js
    // refresh, customizer preview reloads). Watch for the root to appear.
    LOG('bootstrap: waiting for [data-gas-card-root] via MutationObserver');
    var mo = new MutationObserver(function () {
      if (init()) {
        subscribeToCartUpdates();
        mo.disconnect();
      }
    });
    mo.observe(document.body || document.documentElement, { childList: true, subtree: true });
    // Belt and suspenders: stop watching after 30s to avoid leaking
    setTimeout(function () { mo.disconnect(); }, 30000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
